import { memo, useCallback, useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { usePress } from '@react-aria/interactions'
import cn from 'classnames'

import { notifications } from '~/shared/lib/notifications'
import type { NotificationItem } from '~/shared/lib/notifications'
import { Notification } from '~/shared/ui/Notification'

import { useNotificationSwipeUp } from './hooks'

import styles from './AnimatedNotification.module.scss'

type AnimatedNotificationProps = {
  item: NotificationItem
}

/**
 * Обёртка над презентационным Notification: появление сверху вниз / уход вверх — на внешнем
 * узле (позиционный transition), отклик на тап scale(.97) — на внутреннем (usePress).
 * Разные узлы специально: анимация позиции и press-scale обе меняют transform, на одном
 * элементе они бы перетирали друг друга.
 *
 * Истечение по таймеру считает стор (shared/lib/notifications), тут только реакция на него:
 * тап и свайп вверх досрочно завершают ожидание (notifications.expire), а после того как
 * transition ухода доиграл — notifications.remove убирает элемент из стека.
 */
export const AnimatedNotification = memo(function AnimatedNotification(
  props: AnimatedNotificationProps,
) {
  const { item } = props
  const [isEntered, setIsEntered] = useState(false)

  useEffect(() => {
    // Стартуем из исходного состояния (сдвинуто вверх, прозрачно) и на следующий кадр
    // переключаем в конечное — только так CSS-transition реально проигрывается при маунте.
    const frame = requestAnimationFrame(() => setIsEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const dismiss = useCallback(() => {
    notifications.expire(item.id)
  }, [item.id])

  const onLeaveTransitionEnd = useCallback(() => {
    if (item.isLeaving) notifications.remove(item.id)
  }, [item.id, item.isLeaving])

  const { translateY, isDragging, resetDrag, swipeHandlers } = useNotificationSwipeUp(dismiss)
  const { pressProps, isPressed } = usePress({ onPress: dismiss })

  useEffect(() => {
    // Как только стор пометил уведомление истёкшим — отдаём transform обратно классам
    // (.leaving), иначе замороженный inline-style драга перекрыл бы анимацию ухода.
    if (item.isLeaving) resetDrag()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.isLeaving])

  const dragStyle: CSSProperties | undefined = isDragging
    ? { transform: `translateY(${translateY}px)`, transitionDuration: '0ms' }
    : undefined

  return (
    <div
      className={cn(styles.position, isEntered && styles.entered, item.isLeaving && styles.leaving)}
      style={dragStyle}
      onTransitionEnd={onLeaveTransitionEnd}
      {...swipeHandlers}
    >
      <div className={styles.pressable} data-pressed={isPressed} {...pressProps}>
        <Notification theme={item.theme} title={item.title} text={item.text} onClose={dismiss} />
      </div>
    </div>
  )
})
